// Backend · Alerts: the editors' client-side checks. Each mirrors a server rule
// so a refusal is explained before the request, never instead of it: the
// server's message is still what shows when it disagrees.

export interface NumericFieldOptions {
  integer?: boolean;
  min?: number;
  max?: number;
}

export function numericFieldError(
  value: string | number | null | undefined,
  label: string,
  {
    integer = false,
    min = undefined,
    max = undefined,
  }: NumericFieldOptions = {},
): string | null {
  const text = String(value ?? '').trim();
  if (!text) return `${label} is required.`;
  const number = Number(text);
  if (!Number.isFinite(number)) return `${label} must be a valid number.`;
  if (integer && !Number.isInteger(number)) return `${label} must be a whole number.`;
  if (min !== undefined && number < min) return `${label} must be at least ${min}.`;
  if (max !== undefined && number > max) return `${label} must be at most ${max}.`;
  return null;
}

// Mirrors the built-in list in `shared::WEBHOOK_BUILT_IN_HOSTS`, which the API
// and batch-worker both use. Note the server may additionally accept an
// operator-allowlisted host that the browser cannot know about, so this is a
// hint, not the authority — the server's message is what gets shown on refusal.
export const WEBHOOK_PROVIDER_HOSTS: readonly string[] = ['hooks.slack.com', 'discord.com', 'discordapp.com'];

export function isValidWebhookUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    const supportedProvider = WEBHOOK_PROVIDER_HOSTS.some(
      (base) => host === base || host.endsWith(`.${base}`),
    );
    return url.protocol === 'https:'
      && !!host
      && supportedProvider
      && !url.username
      && !url.password
      && !url.hash
      && host !== 'localhost'
      && !host.endsWith('.localhost')
      && !host.endsWith('.local')
      && !host.endsWith('.internal');
  } catch {
    return false;
  }
}

// Mirror of `shared::validation::validate_email`. The previous pattern was far
// looser — it accepted `a@b.c` (1-char TLD), apostrophes, commas and `&` — so
// addresses that passed here were rejected by the API, and a channel already
// holding one could not even be renamed.
export const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
/** Server-side caps in `validate_notification_channel` (api/src/routes/admin.rs). */
export const CHANNEL_NAME_MAX = 200;
export const CHANNEL_RECIPIENTS_MAX = 100;
export const RECIPIENT_ADDRESS_MAX = 254;
